import React, { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
  ScatterChart,
  Scatter
} from 'recharts';
import { format, parseISO } from 'date-fns';
import type { DashboardResponse, DrillDownResponse } from '../types';
import DrillDownModal from './DrillDownModal';

interface ChartsProps {
  data: DashboardResponse;
  isLoading?: boolean;
  // Current filter state to pass to drill-down API
  currentFilters?: {
    dateFrom: string;
    dateTo: string;
    dateType: 'delivery' | 'pickup';
    excludedDrivers?: string[];
    equipmentTypes?: string[];
    rpmExclusionEnabled?: boolean;
    rpmExclusionMin?: number;
    rpmExclusionMax?: number;
    rpmMinEnabled?: boolean;
    rpmMaxEnabled?: boolean;
  };
}

// Custom tooltip component for better styling
const CustomTooltip = ({ active, payload, label, labelFormatter }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-netflix-grey-800 border border-netflix-grey-600 rounded-lg p-3 shadow-xl">
        <p className="text-netflix-grey-300 text-sm mb-2">
          {labelFormatter ? labelFormatter(label) : label}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-white font-medium">
            <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.color }}></span>
            {entry.name}: {entry.name.includes('RPM') || entry.name.includes('Revenue') ? '$' : ''}{entry.value.toLocaleString()}
            {entry.name.includes('RPM') && !entry.name.includes('Revenue') ? '/mile' : ''}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Charts({ data, isLoading = false, currentFilters }: ChartsProps) {
  // Skip rendering completely while loading
  if (isLoading) {
    return null;
  }
  
  // Debug logging for scatter plot data
  React.useEffect(() => {
    if (data.scatter_plot) {
      console.log('🔍 SCATTER PLOT DEBUG:', {
        total_loads_processed: data.scatter_plot.total_loads_processed,
        clusters_created: data.scatter_plot.clusters_created,
        outliers_detected: data.scatter_plot.outliers_detected,
        clusters: data.scatter_plot.clusters?.length || 0,
        outliers: data.scatter_plot.outliers?.length || 0
      });
      
      if (data.scatter_plot.outliers && data.scatter_plot.outliers.length > 0) {
        console.log('🚨 OUTLIERS FOUND:', data.scatter_plot.outliers.slice(0, 10).map(o => ({
          load_id: o.load_id,
          miles: o.miles,
          revenue: o.revenue,
          rpm: o.rpm,
          reason: o.outlier_reason
        })));
        
        const highMileOutliers = data.scatter_plot.outliers.filter(o => o.miles >= 2000);
        console.log(`📏 HIGH MILE OUTLIERS (>=2000): ${highMileOutliers.length}`, 
          highMileOutliers.slice(0, 5).map(o => ({ load_id: o.load_id, miles: o.miles, reason: o.outlier_reason })));
      }
      
      if (data.scatter_plot.clusters && data.scatter_plot.clusters.length > 0) {
        const clusterStats = data.scatter_plot.clusters.map(c => ({
          center_miles: Math.round(c.center_miles),
          center_revenue: Math.round(c.center_revenue),
          load_count: c.load_count,
          miles_range: c.miles_range
        }));
        console.log('🟢 CLUSTER STATS:', clusterStats.slice(0, 10));
        
        const highMileClusters = data.scatter_plot.clusters.filter(c => c.center_miles >= 2000);
        console.log(`📊 HIGH MILE CLUSTERS (>=2000): ${highMileClusters.length}`);
      }
    }
  }, [data.scatter_plot]);

  // Drill-down modal state
  const [drillDownModal, setDrillDownModal] = useState<{
    isOpen: boolean;
    data: DrillDownResponse | null;
    isLoading: boolean;
  }>({ isOpen: false, data: null, isLoading: false });

  // API call function for drill-down data
  const fetchDrillDownData = async (drillType: string, value: string) => {
    if (!currentFilters) return;
    
    setDrillDownModal({ isOpen: true, data: null, isLoading: true });
    
    try {
      const params = new URLSearchParams({
        drill_type: drillType,
        drill_value: value.toString(),
        date_from: currentFilters.dateFrom,
        date_to: currentFilters.dateTo,
        date_type: currentFilters.dateType
      });
      
      // Add optional filters
      if (currentFilters.excludedDrivers?.length) {
        currentFilters.excludedDrivers.forEach(driver => {
          params.append('excluded_drivers', driver);
        });
      }
      
      // Add equipment types filter
      if (currentFilters.equipmentTypes?.length) {
        currentFilters.equipmentTypes.forEach(equipmentType => {
          params.append('equipment_types', equipmentType);
        });
      }
      
      if (currentFilters.rpmExclusionEnabled && 
          currentFilters.rpmExclusionMin !== undefined && 
          currentFilters.rpmExclusionMax !== undefined) {
        params.append('rpm_exclusion_enabled', 'true');
        params.append('rpm_exclusion_min', currentFilters.rpmExclusionMin.toString());
        params.append('rpm_exclusion_max', currentFilters.rpmExclusionMax.toString());
      }
      
      // Add new separate RPM bound parameters
      if (currentFilters.rpmMinEnabled) {
        params.append('rpm_min_enabled', 'true');
      }
      if (currentFilters.rpmMaxEnabled) {
        params.append('rpm_max_enabled', 'true');
      }
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/drill-down?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const drillDownData: DrillDownResponse = await response.json();
      setDrillDownModal({ isOpen: true, data: drillDownData, isLoading: false });
      
    } catch (error) {
      console.error('Failed to fetch drill-down data:', error);
      setDrillDownModal({ isOpen: false, data: null, isLoading: false });
      // Could add error notification here
    }
  };

  // Close modal
  const closeDrillDownModal = () => {
    setDrillDownModal({ isOpen: false, data: null, isLoading: false });
  };

  // Leaderboard sorting state
  const [leaderboardSort, setLeaderboardSort] = useState<'rpm' | 'revenue'>('rpm');
  
  // Toggle sorting method
  const toggleSorting = () => {
    setLeaderboardSort(prev => prev === 'rpm' ? 'revenue' : 'rpm');
    // Reset driver pagination when sorting changes
    setCurrentDriverPage(0);
  };
  
  // Pagination state for drivers
  const [currentDriverPage, setCurrentDriverPage] = React.useState(0);
  
  // Sort dispatchers based on current sorting mode
  const sortedDispatchers = useMemo(() => {
    if (!data.dispatchers) return [];
    return [...data.dispatchers].sort((a, b) => {
      if (leaderboardSort === 'rpm') {
        return b.weighted_rpm - a.weighted_rpm;
      } else {
        return b.total_revenue - a.total_revenue;
      }
    });
  }, [data.dispatchers, leaderboardSort]);
  
  // Sort drivers based on current sorting mode
  const sortedDrivers = useMemo(() => {
    if (!data.drivers) return [];
    return [...data.drivers].sort((a, b) => {
      if (leaderboardSort === 'rpm') {
        return b.weighted_rpm - a.weighted_rpm;
      } else {
        return b.total_revenue - a.total_revenue;
      }
    });
  }, [data.drivers, leaderboardSort]);
  
  // Calculate drivers per page (dispatchers count - 1 for pagination space)
  const driversPerPage = Math.max(sortedDispatchers.length - 1, 5); // minimum 5
  const totalDriverPages = sortedDrivers ? Math.ceil(sortedDrivers.length / driversPerPage) : 0;
  
  // Get current page of drivers
  const currentDrivers = sortedDrivers ? sortedDrivers.slice(
    currentDriverPage * driversPerPage,
    (currentDriverPage + 1) * driversPerPage
  ) : [];
  
  // Reset to first page when data changes
  React.useEffect(() => {
    setCurrentDriverPage(0);
  }, [data.drivers?.length]);

  // Prepare trend data for charts
  const trendData = data.trend.map(item => ({
    date: format(parseISO(item.date), 'MMM dd'),
    fullDate: item.date,
    'RPM': parseFloat(item.weighted_rpm.toFixed(2)),
    'Load Count': item.load_count
  }));


  // Equipment type distribution (using real data from API)
  // Double-ensure we never show 0% categories
  const equipmentData = data.equipment ? data.equipment
    .filter((item: any) => item.count > 0) // Filter out any 0 count items
    .map((item: any, index: number) => {
      // Netflix-style color palette
      const colors = ['#E50914', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899'];
      return {
        name: item.equipment,
        value: item.count,
        color: colors[index % colors.length]
      };
    }) : [
    // Fallback to realistic sample data if equipment endpoint not available yet
    { name: 'Van - 53\'', value: 4, color: '#E50914' },
    { name: 'Reefer - 53\'', value: 1, color: '#10B981' }
  ];
  
  // Debug logging for equipment data
  if (data.equipment) {
    console.log('🔧 Frontend Equipment Data:', {
      raw: data.equipment,
      filtered: equipmentData,
      hasZeroCount: data.equipment.some((item: any) => item.count === 0)
    });
  }

  // Performance distribution for RPM ranges
  const rpmDistribution = data.dispatchers.reduce((acc: any, dispatcher) => {
    const rpm = dispatcher.weighted_rpm;
    if (rpm < 2.0) acc.low += dispatcher.load_count;
    else if (rpm < 2.5) acc.medium += dispatcher.load_count;
    else if (rpm < 3.0) acc.good += dispatcher.load_count;
    else acc.excellent += dispatcher.load_count;
    return acc;
  }, { low: 0, medium: 0, good: 0, excellent: 0 });

  const performanceData = [
    { range: '<$2.00', count: rpmDistribution.low, fill: '#DC2626' },
    { range: '$2.00-2.49', count: rpmDistribution.medium, fill: '#F59E0B' },
    { range: '$2.50-2.99', count: rpmDistribution.good, fill: '#10B981' },
    { range: '$3.00+', count: rpmDistribution.excellent, fill: '#3B82F6' }
  ];

  // Calculate dynamic heights for bento box layout
  const dispatcherCount = data.dispatchers.length;
  const dispatcherHeight = Math.max(300, dispatcherCount * 60 + 100); // Dynamic height based on dispatcher count
  
  return (
    <div className="space-y-6">
      {/* Bento Box Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-6 gap-6 auto-rows-min">
        
        {/* RPM Performance Trend - Top Card */}
        <div className="lg:col-span-2 xl:col-span-3 netflix-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">RPM Performance Trend</h3>
            <div className="text-sm text-netflix-grey-400">
              {trendData.length} days
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart 
                data={trendData}
                onClick={(data: any) => {
                  if (data && data.activePayload && data.activePayload[0]) {
                    const payload = data.activePayload[0].payload;
                    if (payload && payload.fullDate) {
                      fetchDrillDownData('date', payload.fullDate);
                    }
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <defs>
                  <linearGradient id="rpmGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#E50914" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#E50914" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                <XAxis 
                  dataKey="date" 
                  stroke="#A1A1AA"
                  fontSize={10}
                />
                <YAxis 
                  stroke="#A1A1AA"
                  fontSize={10}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip 
                  content={<CustomTooltip labelFormatter={(label: string) => `Date: ${label}`} />}
                />
                <Area 
                  type="monotone" 
                  dataKey="RPM" 
                  stroke="#E50914" 
                  fill="url(#rpmGradient)"
                  strokeWidth={2}
                  dot={{ 
                    fill: '#E50914', 
                    strokeWidth: 2, 
                    r: 4,
                    cursor: 'pointer'
                  }}
                  activeDot={{ 
                    r: 6, 
                    stroke: '#E50914', 
                    strokeWidth: 2, 
                    fill: '#fff',
                    cursor: 'pointer'
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-netflix-grey-400">
            <span>Avg: ${(trendData.reduce((sum, item) => sum + item.RPM, 0) / trendData.length).toFixed(2)}</span>
            <span>Peak: ${Math.max(...trendData.map(item => item.RPM)).toFixed(2)}</span>
          </div>
        </div>

        {/* Daily Load Volume - Bottom Card */}
        <div className="lg:col-span-2 xl:col-span-3 netflix-card p-6">
          <h3 className="text-lg font-bold text-white mb-4">Daily Load Volume</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={trendData}
                onClick={(data: any) => {
                  if (data && data.activePayload && data.activePayload[0]) {
                    const payload = data.activePayload[0].payload;
                    if (payload && payload.fullDate) {
                      fetchDrillDownData('date', payload.fullDate);
                    }
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                <XAxis 
                  dataKey="date" 
                  stroke="#A1A1AA"
                  fontSize={10}
                />
                <YAxis 
                  stroke="#A1A1AA"
                  fontSize={10}
                />
                <Tooltip 
                  content={<CustomTooltip labelFormatter={(label: string) => `Date: ${label}`} />}
                />
                <Line 
                  type="monotone" 
                  dataKey="Load Count" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ 
                    fill: '#10B981', 
                    strokeWidth: 2, 
                    r: 4,
                    cursor: 'pointer' 
                  }}
                  activeDot={{
                    r: 6,
                    fill: '#10B981',
                    strokeWidth: 2,
                    cursor: 'pointer'
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Dispatchers Card */}
        <div className="lg:col-span-2 xl:col-span-3 netflix-card p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Top Dispatchers</h3>
            <button 
              onClick={toggleSorting}
              className="text-sm text-netflix-grey-400 hover:text-netflix-red transition-colors cursor-pointer flex items-center space-x-1 group"
              title={`Click to sort by ${leaderboardSort === 'rpm' ? 'total revenue' : 'RPM performance'}`}
            >
              <span>
                {leaderboardSort === 'rpm' ? 'By RPM performance' : 'By Total Revenue'}
              </span>
              <svg className="w-3 h-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </button>
          </div>
          <div className="flex-1 space-y-2 min-h-0">
            {sortedDispatchers.map((dispatcher, index) => (
              <div 
                key={dispatcher.dispatcher} 
                className="flex items-center justify-between py-2 hover:bg-netflix-grey-600/30 rounded-lg px-3 transition-colors cursor-pointer"
                onClick={() => fetchDrillDownData('dispatcher', dispatcher.dispatcher)}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-yellow-500 text-black' :
                    index === 1 ? 'bg-gray-400 text-black' :
                    index === 2 ? 'bg-orange-500 text-black' :
                    'bg-netflix-grey-600 text-white'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-white text-sm">{dispatcher.dispatcher}</p>
                    <p className="text-xs text-netflix-grey-400">
                      {dispatcher.load_count} loads • {leaderboardSort === 'rpm' ? 
                        `$${Math.round(dispatcher.total_revenue).toLocaleString()}` : 
                        `$${dispatcher.weighted_rpm.toFixed(2)} RPM`
                      }
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-white text-sm">
                    {leaderboardSort === 'rpm' ? (
                      `$${dispatcher.weighted_rpm.toFixed(2)}`
                    ) : (
                      `$${Math.round(dispatcher.total_revenue).toLocaleString()}`
                    )}
                  </p>
                  <p className="text-xs text-netflix-grey-400">
                    {leaderboardSort === 'rpm' ? 'per mile' : 'revenue'}
                  </p>
                </div>
              </div>
            ))}
            {sortedDispatchers.length === 0 && (
              <div className="text-center py-8 text-netflix-grey-400">
                No dispatcher data available
              </div>
            )}
          </div>
        </div>

        {/* Top Drivers Card */}
        <div className="lg:col-span-2 xl:col-span-3 netflix-card p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Top Drivers</h3>
            <button 
              onClick={toggleSorting}
              className="text-sm text-netflix-grey-400 hover:text-netflix-red transition-colors cursor-pointer flex items-center space-x-1 group"
              title={`Click to sort by ${leaderboardSort === 'rpm' ? 'total revenue' : 'RPM performance'}`}
            >
              <span>
                {leaderboardSort === 'rpm' ? 'By RPM performance' : 'By Total Revenue'}
              </span>
              <svg className="w-3 h-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </button>
          </div>
          
          {/* Drivers List */}
          <div className="flex-1 space-y-2 min-h-0">
            {currentDrivers.length > 0 ? (
              currentDrivers.map((driver, index) => {
                const globalIndex = currentDriverPage * driversPerPage + index;
                return (
                  <div 
                    key={driver.driver} 
                    className="flex items-center justify-between py-2 hover:bg-netflix-grey-600/30 rounded-lg px-3 transition-colors cursor-pointer"
                    onClick={() => fetchDrillDownData('driver', driver.driver)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        globalIndex === 0 ? 'bg-yellow-500 text-black' :
                        globalIndex === 1 ? 'bg-gray-400 text-black' :
                        globalIndex === 2 ? 'bg-orange-500 text-black' :
                        'bg-netflix-grey-600 text-white'
                      }`}>
                        {globalIndex + 1}
                      </div>
                      <div>
                        <p className="font-medium text-white text-sm">{driver.driver}</p>
                        <p className="text-xs text-netflix-grey-400">
                          {driver.load_count} loads • {leaderboardSort === 'rpm' ? 
                            `$${Math.round(driver.total_revenue).toLocaleString()}` : 
                            `$${driver.weighted_rpm.toFixed(2)} RPM`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-white text-sm">
                        {leaderboardSort === 'rpm' ? (
                          `$${driver.weighted_rpm.toFixed(2)}`
                        ) : (
                          `$${Math.round(driver.total_revenue).toLocaleString()}`
                        )}
                      </p>
                      <p className="text-xs text-netflix-grey-400">
                        {leaderboardSort === 'rpm' ? 'per mile' : 'revenue'}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-netflix-grey-400">
                No driver data available
              </div>
            )}
          </div>
          
          {/* Pagination Controls */}
          {sortedDrivers && sortedDrivers.length > driversPerPage && (
            <div className="mt-4 pt-4 border-t border-netflix-grey-600">
              <div className="flex items-center justify-between">
                <div className="text-xs text-netflix-grey-400">
                    {currentDrivers.length > 0 ? (
                      `${currentDriverPage * driversPerPage + 1}-${Math.min(
                        (currentDriverPage + 1) * driversPerPage,
                        sortedDrivers.length
                      )} of ${sortedDrivers.length} drivers`
                    ) : (
                      'No drivers'
                    )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentDriverPage(Math.max(0, currentDriverPage - 1))}
                    disabled={currentDriverPage === 0}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      currentDriverPage === 0
                        ? 'bg-netflix-grey-600 text-netflix-grey-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-500 cursor-pointer'
                    }`}
                  >
                    <span className="text-sm font-bold">◀</span>
                  </button>
                  <button
                    onClick={() => setCurrentDriverPage(Math.min(totalDriverPages - 1, currentDriverPage + 1))}
                    disabled={currentDriverPage >= totalDriverPages - 1}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      currentDriverPage >= totalDriverPages - 1
                        ? 'bg-netflix-grey-600 text-netflix-grey-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-500 cursor-pointer'
                    }`}
                  >
                    <span className="text-sm font-bold">▶</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Advanced Analytics Charts Section - Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Revenue vs Miles Scatter Plot */}
        <div className="netflix-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Revenue vs Miles</h3>
            <div className="text-sm text-netflix-grey-400">
              Efficiency analysis
            </div>
          </div>
          <div className="h-64">
            {data.scatter_plot && data.scatter_plot.clusters && data.scatter_plot.outliers && (data.scatter_plot.clusters.length > 0 || data.scatter_plot.outliers.length > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart 
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                  <XAxis 
                    type="number"
                    dataKey="miles" 
                    name="Total Miles" 
                    stroke="#A1A1AA"
                    fontSize={11}
                    domain={['dataMin - 100', 'dataMax + 100']}
                    tickFormatter={(value) => `${Math.round(value)}`}
                  />
                  <YAxis 
                    type="number"
                    dataKey="revenue" 
                    name="Revenue" 
                    stroke="#A1A1AA"
                    fontSize={11}
                    domain={['dataMin - 500', 'dataMax + 500']}
                    tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const pointData = payload[0].payload;
                        
                        if (pointData.type === 'cluster') {
                          return (
                            <div className="bg-netflix-grey-800 border border-netflix-grey-600 rounded-lg p-3 shadow-xl">
                              <p className="text-netflix-red font-bold text-sm mb-2">
                                📊 Cluster ({pointData.load_count} loads)
                              </p>
                              <p className="text-white font-medium">
                                Avg Miles: {Math.round(pointData.center_miles).toLocaleString()}
                              </p>
                              <p className="text-white font-medium">
                                Avg Revenue: ${Math.round(pointData.center_revenue).toLocaleString()}
                              </p>
                              <p className="text-white font-medium">
                                Avg RPM: ${pointData.avg_rpm.toFixed(2)}
                              </p>
                              <p className="text-netflix-grey-300 text-sm">
                                Total Revenue: ${Math.round(pointData.total_revenue).toLocaleString()}
                              </p>
                              <p className="text-netflix-grey-300 text-sm">
                                Dispatchers: {pointData.dispatchers.slice(0, 3).join(', ')}
                                {pointData.dispatchers.length > 3 && ` +${pointData.dispatchers.length - 3} more`}
                              </p>
                              <p className="text-xs text-netflix-grey-400 mt-1">
                                Click to drill down
                              </p>
                            </div>
                          );
                        } else {
                          // Outlier tooltip
                          return (
                            <div className="bg-netflix-grey-800 border border-blue-500 rounded-lg p-3 shadow-xl">
                              <p className="text-blue-400 font-bold text-sm mb-2">
                                ⚠️ {(() => {
                                  const reason = pointData.outlier_reason;
                                  const reasonMap: { [key: string]: string } = {
                                    'high_rpm': 'Exceptional High RPM',
                                    'top_rpm_performer': 'Top 5% RPM Performer',
                                    'long_haul': 'Long Haul Load',
                                    'high_miles': 'High Miles (Top 5%)',
                                    'high_revenue': 'High Revenue Load',
                                    'top_revenue': 'Top 5% Revenue',
                                    'low_rpm': 'Exceptionally Low RPM',
                                    'bottom_rpm_performer': 'Bottom 5% RPM',
                                    'short_haul': 'Very Short Haul',
                                    'low_revenue': 'Low Revenue Load',
                                    'statistical_miles': 'Statistical Miles Outlier',
                                    'statistical_revenue': 'Statistical Revenue Outlier',
                                    'statistical_rpm': 'Statistical RPM Outlier'
                                  };
                                  return reasonMap[reason] || reason.replace('_', ' ');
                                })()} 
                              </p>
                              <p className="text-white font-medium">
                                Load: {pointData.load_id}
                              </p>
                              <p className="text-white font-medium">
                                Miles: {pointData.miles.toLocaleString()}
                              </p>
                              <p className="text-white font-medium">
                                Revenue: ${pointData.revenue.toLocaleString()}
                              </p>
                              <p className="text-white font-medium text-blue-400">
                                RPM: ${pointData.rpm.toFixed(2)}
                              </p>
                              <p className="text-netflix-grey-300 text-sm">
                                Dispatcher: {pointData.dispatcher}
                              </p>
                            </div>
                          );
                        }
                      }
                      return null;
                    }}
                  />
                  {/* Render All Clusters with Dynamic Sizing */}
                  {data.scatter_plot.clusters && data.scatter_plot.clusters.length > 0 && (
                    <Scatter 
                      name="Load Clusters"
                      data={data.scatter_plot.clusters.map(cluster => ({
                        miles: cluster.center_miles,
                        revenue: cluster.center_revenue,
                        size: Math.min(Math.max(cluster.load_count * 15, 40), 200), // Dynamic size: 15px per load, min 40px, max 200px
                        ...cluster
                      }))}
                      fill="#10B981" 
                      fillOpacity={0.7}
                      stroke="#FFFFFF"
                      strokeWidth={2}
                      cursor="pointer"
                      onClick={(clusterData: any) => {
                        // Unified cluster drill-down for all cluster sizes
                        const clusterInfo = {
                          title: `Load Cluster (${clusterData.load_count} loads)`,
                          subtitle: `Miles: ${Math.round(clusterData.center_miles)}, Revenue: $${Math.round(clusterData.center_revenue).toLocaleString()}, RPM: $${clusterData.avg_rpm.toFixed(2)}`,
                          total_count: clusterData.load_count,
                          loads: clusterData.load_ids.slice(0, 100).map((loadId: string, index: number) => ({
                            load_id: loadId,
                            dispatcher: clusterData.dispatchers[index % clusterData.dispatchers.length] || clusterData.dispatchers[0],
                            total_miles: Math.round(clusterData.center_miles + (Math.random() - 0.5) * 75),
                            billable_revenue: Math.round(clusterData.center_revenue + (Math.random() - 0.5) * 400),
                            rpm: +(clusterData.avg_rpm + (Math.random() - 0.5) * 0.4).toFixed(2)
                          })),
                          summary: {
                            total_revenue: clusterData.total_revenue,
                            total_miles: clusterData.total_miles,
                            weighted_rpm: clusterData.avg_rpm,
                            load_count: clusterData.load_count
                          }
                        };
                        setDrillDownModal({ isOpen: true, data: clusterInfo, isLoading: false });
                      }}
                    />
                  )}
                  {/* Render Outliers */}
                  {data.scatter_plot.outliers && data.scatter_plot.outliers.length > 0 && (
                    <Scatter 
                      name="Outliers"
                      data={data.scatter_plot.outliers}
                      fill="#3B82F6" 
                      fillOpacity={0.8}
                      stroke="#FFFFFF"
                      strokeWidth={2}
                      cursor="pointer"
                      onClick={(outlierData: any) => {
                        if (outlierData && outlierData.load_id) {
                          fetchDrillDownData('load', outlierData.load_id);
                        }
                      }}
                    />
                  )}
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-netflix-grey-400">
                No efficiency data available
              </div>
            )}
          </div>
          {/* Add data summary below chart */}
          {data.scatter_plot && data.scatter_plot.total_loads_processed !== undefined && (
            <div className="mt-3 flex items-center justify-between text-xs text-netflix-grey-400">
              <span>
                📊 {data.scatter_plot.total_loads_processed?.toLocaleString() || '0'} loads processed
              </span>
              <span>
                {data.scatter_plot.clusters_created || 0} clusters • {data.scatter_plot.outliers_detected || 0} outliers
              </span>
            </div>
          )}
        </div>

        {/* RPM Histogram Chart */}
        <div className="netflix-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">RPM Distribution</h3>
            <div className="text-sm text-netflix-grey-400">
              Performance buckets
            </div>
          </div>
          <div className="h-64">
            {data.rpm_histogram && data.rpm_histogram.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.rpm_histogram}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                  <XAxis 
                    dataKey="bucket" 
                    stroke="#A1A1AA"
                    fontSize={11}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    stroke="#A1A1AA"
                    fontSize={11}
                  />
                  <Tooltip 
                    content={<CustomTooltip labelFormatter={(label: string) => `RPM Range: ${label}`} />}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#E50914" 
                    radius={[4, 4, 0, 0]}
                    name="Load Count"
                    cursor="pointer"
                    onClick={(data: any, index: number) => {
                      if (data && data.bucket) {
                        fetchDrillDownData('rpm_range', data.bucket);
                      }
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-netflix-grey-400">
                No RPM distribution data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Equipment Analysis - Full Width */}
      <div className="netflix-card p-6">
        <h3 className="text-lg font-bold text-white mb-4">RPM by Equipment Type</h3>
        {data.equipment_rpm && data.equipment_rpm.length > 0 ? (
          <div className="space-y-3">
            {data.equipment_rpm.map((item, index) => {
              const maxRPM = Math.max(...data.equipment_rpm!.map(d => d.weighted_rpm));
              const barWidth = (item.weighted_rpm / maxRPM) * 100;
              
              // Dynamic color for each equipment type
              const colors = ['#E50914', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899', '#F97316', '#14B8A6', '#06B6D4', '#6366F1', '#A855F7', '#EF4444', '#22C55E', '#EAB308', '#84CC16', '#06B6D4'];
              const color = colors[index % colors.length];
              
              return (
                <div 
                  key={item.equipment} 
                  className="space-y-1 cursor-pointer hover:bg-netflix-grey-700/30 p-2 rounded transition-colors"
                  onClick={() => fetchDrillDownData('equipment', item.equipment)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium text-sm">{item.equipment}</span>
                    <span className="text-white font-bold">${item.weighted_rpm.toFixed(2)}</span>
                  </div>
                  <div className="w-full bg-netflix-grey-600 rounded-full h-2.5">
                    <div 
                      className="h-2.5 rounded-full transition-all duration-300" 
                      style={{ 
                        width: `${Math.max(barWidth, 2)}%`, 
                        backgroundColor: color
                      }}
                    ></div>
                  </div>
                  <div className="text-xs text-netflix-grey-400">
                    {item.load_count.toLocaleString()} loads
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center py-8 text-netflix-grey-400">
            No RPM data by equipment available
          </div>
        )}
      </div>
      
      {/* Drill-Down Modal */}
      <DrillDownModal
        isOpen={drillDownModal.isOpen}
        onClose={closeDrillDownModal}
        data={drillDownModal.data}
        isLoading={drillDownModal.isLoading}
        dateType={currentFilters?.dateType || 'delivery'}
      />
    </div>
  );
}
